import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { LoadingButton } from '@olxbr/ds-core'
import { Container, TextInput, Checkbox, withMargins } from '@olxbr/ds-web'
import { DocumentTypeSelection } from '../../molecules/DocumentTypeSelection'
import { DocumentInput } from '../../atoms/DocumentInput'
import { isValidDocument, getUnmaskedDocument } from '../../../util/document'
import { maxNumbers } from '../../../util/validators'
import getSessionTokenInfos from '../../../util/sessionTokenGetter'
import { PersonalDataTooltip } from '../../organisms/PersonalDataTooltip'
import EmailChip from '../../atoms/EmailChip'
import Row from '../../atoms/Row'
import Lurker from '../../../util/lurker'
import { updateDataAfterSocialLogin } from '../../../api/updateAccount'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import { View } from '../../atoms/View'

const lurker = new Lurker('document_input')

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  @media (max-width: 440px) {
    gap: var(--spacing-3);
  }
`

const CardBox = styled(Container)`
  max-width: 468px;
  padding: 32px 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: visible;
`

const ParentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
`

const TextDescricption = styled.span.attrs({ className: 'typo-body-medium' })`
  text-align: center;
  font-size: 17px;
`

const RowWrapper = styled(Row)`
  margin-bottom: var(--spacing-3);
  ${withMargins}
`

const GenericErrorMsgWrapper = styled(View)`
  margin-top: 24px;
  margin-bottom: 24px;
`

const SocialLoginTypingTemplate = ({ token, email, onPhoneNotValidated, from }) => {
  const [legalDocument, setLegalDocument] = useState({
    value: '',
    valid: false,
    pristine: true,
    msg: '',
  })

  const [userType, setUserType] = useState({
    value: 'cpf',
    message: 'Campo obrigatório. Informe o tipo da sua conta.',
  })
  const [isMEI, setIsMEI] = useState(false)

  const [nickname, setNickname] = useState({
    value: '',
    pristine: true,
    valid: false,
    msg: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showGenericError, setShowGenericError] = useState(false)
  const [showNicknameError, setShowNicknameError] = useState(false)
  const [showNCpfError, setShowCpfError] = useState(false)
  const [showRateLimitError, setShowRateLimitError] = useState(false)

  useEffect(() => {
    lurker.pageLoadWithExtraDataEvt('document_input', { from })
  }, [])

  const getDocumentErrorMessage = () => {
    return userType.value === 'cpf'
      ? 'Campo obrigatório. Informe o número do seu CPF.'
      : 'Campo obrigatório. Informe o número do seu CNPJ.'
  }

  const validValue = value =>
    typeof value === 'string' ? value.length >= 3 && maxNumbers(value, 5) : !!value

  const getNickNameErrorMsg = value =>
    value.length < 3
      ? 'Por favor, preencha ao menos 3 caracteres'
      : 'Seu apelido não pode ter mais de 5 caracteres numéricos'

  const onUserTypeChange = event => {
    const value = event.target?.value
    if (typeof value === 'string') {
      setUserType(prev => ({
        ...prev,
        value,
      }))

      setLegalDocument(prev => ({
        ...prev,
        value: '',
        msg: '',
      }))
    }
  }

  const validateLegalDocument = () => {
    if (!legalDocument.value || !isValidDocument(legalDocument.value, userType.value)) {
      setLegalDocument(prev => ({
        ...prev,
        valid: false,
        msg: getDocumentErrorMessage(),
      }))
    } else {
      setLegalDocument(prev => ({
        ...prev,
        valid: true,
        msg: '',
      }))
    }
  }

  const handleLegalDocumentBlur = () => {
    validateLegalDocument()
  }

  const onMEIChange = checked => {
    setIsMEI(checked)
  }

  const onNicknameChange = event => {
    const value = event.target?.value

    setNickname(prev => ({
      ...prev,
      value,
      valid: validValue(value),
      msg: '',
    }))
  }

  const onNicknameBlur = () => {
    const { value } = nickname

    const isValid = validValue(value)
    setNickname(prev => ({
      ...prev,
      pristine: false,
      valid: isValid,
      msg: isValid ? '' : getNickNameErrorMsg(value),
    }))
  }

  const onSubmit = async () => {
    setIsSubmitting(true)

    lurker.clickEvt('submit', {
      doc_type: userType.value,
      is_mei: isMEI,
    })

    const sessionIds = await getSessionTokenInfos()

    let requestBody = {
      document: getUnmaskedDocument(legalDocument.value),
      userType: userType.value,
      nickname: nickname.value,
      token,
      sessionIds,
    }

    if (isMEI !== null && isMEI !== undefined && userType?.value === 'cnpj') {
      requestBody = {
        ...requestBody,
        isMei: isMEI,
      }
    }

    await updateDataAfterSocialLogin(requestBody)
      .catch(error => {
        const { response = {} } = error
        const { status, data } = response

        const errorMessage =
          typeof data?.message === 'string'
            ? data.message
            : Array.isArray(data?.message) && data.message.length > 0
            ? data.message[0]
            : ''

        if (errorMessage === 'PHONE_NOT_VALIDATED') {
          lurker.viewMessageEvt('success')
          lurker.viewMessageEvt('account_created_success', { from })
          onPhoneNotValidated(response?.data?.token)
        } else if (status === 428) {
          setShowRateLimitError(true)
        } else if (status === 403) {
          if (errorMessage === 'NICKNAME_IN_BLACKLIST') {
            setShowNicknameError(true)
            setNickname(prev => ({
              ...prev,
              value: '',
              valid: false,
              msg: 'Este campo contém palavras que não podem ser utilizadas.',
            }))
          } else if (errorMessage === 'CPF_IN_BLOCKLIST') {
            setShowCpfError(true)
            setLegalDocument(prev => ({
              ...prev,
              value: '',
              valid: false,
              msg: 'Este CPF não pode ser utilizado.',
            }))
          } else {
            setShowGenericError(true)
          }
        } else if (status === 409) {
          if (errorMessage && (errorMessage.startsWith('CPF') || errorMessage.startsWith('CNPJ'))) {
            setLegalDocument(prev => ({
              ...prev,
              value: '',
              valid: false,
              msg: 'Este documento já possui cadastro.',
            }))
          } else {
            setShowGenericError(true)
          }
        } else {
          setShowGenericError(true)
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <ParentContainer>
      <CardBox variant="outlined">
        <FlexWrapper>
          <TextDescricption>
            Informe os dados abaixo para que possamos concluir a criação da sua conta e melhorar a
            sua experiência na OLX.
          </TextDescricption>

          {showGenericError && (
            <GenericErrorMsgWrapper>
              <GenericErrorBox text="Não foi possível salvar seus dados." />
            </GenericErrorMsgWrapper>
          )}
          {showNicknameError && (
            <GenericErrorMsgWrapper>
              <GenericErrorBox text="Este campo contém palavras que não podem ser utilizadas." />
            </GenericErrorMsgWrapper>
          )}
          {showNCpfError && (
            <GenericErrorMsgWrapper>
              <GenericErrorBox text="Este CPF não pode ser utilizado." />
            </GenericErrorMsgWrapper>
          )}
          {showRateLimitError && (
            <GenericErrorMsgWrapper>
              <GenericErrorBox
                showContactUs={false}
                text="Você excedeu o número de tentativas. Por favor, tente novamente mais tarde."
              />
            </GenericErrorMsgWrapper>
          )}

          <EmailChip>{email}</EmailChip>

          <span className="typo-body-medium font-semibold">Escolha o tipo da sua conta</span>
          <RowWrapper>
            <DocumentTypeSelection
              documentType={userType.value}
              disabled={isSubmitting}
              onChange={onUserTypeChange}
            />
          </RowWrapper>

          <DocumentInput
            value={legalDocument.value}
            state={legalDocument.msg ? 'error' : undefined}
            stateMessage={legalDocument.msg}
            onChange={value =>
              setLegalDocument(prev => ({
                ...prev,
                value,
                pristine: false,
              }))
            }
            onBlur={handleLegalDocumentBlur}
            documentType={userType.value}
            disabled={isSubmitting}
            link={<PersonalDataTooltip />}
          />

          {userType.value === 'cnpj' && (
            <Checkbox
              onClick={e => onMEIChange(e.target.checked)}
              aria-label="Checkbox para marcar se você é MEI"
              label="Sou MEI"
              checked={isMEI}
            />
          )}

          <TextInput
            id="nickname"
            label="Como você quer ser chamado(a)?"
            caption="Aparecerá em seu perfil, anúncios e chats."
            value={nickname.value}
            maxLength={100}
            state={nickname.msg ? 'error' : undefined}
            stateMessage={nickname.msg}
            onChange={onNicknameChange}
            onBlur={onNicknameBlur}
            disabled={isSubmitting}
          />

          <LoadingButton
            disabled={!nickname.valid || !legalDocument.valid || isSubmitting}
            isLoading={isSubmitting}
            className="w-full mt-2"
            onClick={() => onSubmit()}
          >
            Criar conta
          </LoadingButton>
        </FlexWrapper>
      </CardBox>
    </ParentContainer>
  )
}

SocialLoginTypingTemplate.propTypes = {
  token: PropTypes.string,
  email: PropTypes.string,
  onPhoneNotValidated: PropTypes.func,
  from: PropTypes.string,
}

export { SocialLoginTypingTemplate }
